import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PercentIcon from '@mui/icons-material/Percent';
import RestoreIcon from '@mui/icons-material/Restore';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import { cardSx, colors, typography } from '../constants';
import { fmt, fmtN, pct, pctVal } from '../lib/format';
import { NumberField, TextField } from './Fields';

const CAT_ICONS = {
  food: ShoppingBagOutlinedIcon,
  labor: PeopleOutlineIcon,
  rent: HomeOutlinedIcon,
  utilities: BoltOutlinedIcon,
  packaging: Inventory2OutlinedIcon,
  marketing: CampaignOutlinedIcon,
  other: DescriptionOutlinedIcon,
};

const catLabelSx = {
  fontWeight: 600,
  fontSize: typography.small,
  color: colors.foreground,
};

const catSubSx = {
  fontSize: typography.caption,
  color: colors.mutedForeground,
  mt: '1px',
};

const smallLabelSx = { fontSize: typography.caption, color: colors.faint };

const headerRowSx = {
  p: 1.75,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
};

const expandSx = {
  borderTop: `1px solid ${colors.border}`,
  backgroundColor: colors.muted,
  p: 1.75,
};

function IconWrap({ icon: Icon, tone }) {
  return (
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: 2,
        flexShrink: 0,
        backgroundColor: tone?.bg ?? colors.muted,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon sx={{ fontSize: 19, color: tone?.fg ?? colors.mutedForeground }} />
    </Box>
  );
}

function Chevron({ expanded }) {
  return expanded ? (
    <ExpandLessIcon sx={{ fontSize: 20, color: colors.primary }} />
  ) : (
    <ExpandMoreIcon sx={{ fontSize: 20, color: colors.faint }} />
  );
}

function ToggleArea({ onToggle, children, sx }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        flex: 1,
        minWidth: 0,
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        fontFamily: 'inherit',
        textAlign: 'left',
        p: 0,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function lineMonthly(l) {
  return l.type === 'hourly' ? (l.wage || 0) * (l.hours || 0) : l.total || 0;
}

function catMonthly(cat) {
  return cat.lines.reduce((s, l) => s + lineMonthly(l), 0);
}

/**
 * A cost the business does not enter: the provider commissions and card
 * processing fee taken out of every sale. It reduces profit like any other
 * category, so it is shown next to them — but with nothing to edit, since it
 * follows the commission set on each channel.
 */
function ReadOnlyCard({ cat, rangeLabel }) {
  return (
    <Box sx={{ ...cardSx, mb: 1.25, overflow: 'hidden' }}>
      <Box sx={headerRowSx}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flex: 1,
            minWidth: 0,
          }}
        >
          <IconWrap icon={LocalShippingOutlinedIcon} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={catLabelSx}>{cat.label}</Typography>
            <Typography sx={catSubSx}>
              {`${fmt(cat.scaledLinesTotal ?? 0)} (${rangeLabel}) · from your channel commissions`}
            </Typography>
          </Box>
        </Box>
        <Typography sx={{ fontWeight: 700, color: colors.primary }}>
          {pct(cat.totalPct)}
        </Typography>
      </Box>
    </Box>
  );
}

function CatCard({
  cat,
  totalRevenue,
  rangeLabel,
  expanded,
  onToggle,
  readOnly,
  onLine,
  onLineName,
  onAddLine,
  onDeleteLine,
  isLineResettable,
  onResetLine,
  getLineDefault,
}) {
  const [suggLine, setSuggLine] = useState(null);
  const monthly = catMonthly(cat);
  const scaledTotal = cat.scaledLinesTotal ?? monthly;
  const hasLines = cat.lines.length > 0;
  const Icon = CAT_ICONS[cat.key] ?? DescriptionOutlinedIcon;
  // The percentage is always derived from recorded costs (line items) against
  // revenue — it is not directly editable. Without line items it falls back to
  // the category's default percentage.
  const derivedPct =
    hasLines && totalRevenue > 0
      ? (scaledTotal / totalRevenue) * 100
      : cat.totalPct;

  return (
    <Box sx={{ ...cardSx, mb: 1.25, overflow: 'hidden' }}>
      <Box sx={headerRowSx}>
        <ToggleArea onToggle={onToggle}>
          <IconWrap icon={Icon} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={catLabelSx}>{cat.label}</Typography>
            <Typography sx={catSubSx}>
              {hasLines
                ? `${cat.lines.length} line item${
                    cat.lines.length > 1 ? 's' : ''
                  } · ${fmt(scaledTotal)} (${rangeLabel})`
                : `${fmt((totalRevenue * cat.totalPct) / 100)} est.`}
            </Typography>
          </Box>
        </ToggleArea>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontWeight: 700, color: colors.primary }}>
            {pct(derivedPct)}
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={onToggle}
            sx={{
              display: 'flex',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              p: 0,
            }}
          >
            <Chevron expanded={expanded} />
          </Box>
        </Box>
      </Box>

      {expanded ? (
        <Box sx={expandSx}>
          {cat.lines.map((line) => {
            const lt = lineMonthly(line);
            const resettable = !!isLineResettable?.(cat.key, line.lineId);
            return (
              <Box
                key={line.lineId}
                sx={{
                  backgroundColor: colors.card,
                  borderRadius: 2.5,
                  p: 1.25,
                  mb: 1,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: line.type === 'hourly' ? 1 : 0,
                  }}
                >
                  {readOnly ? (
                    <Typography
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: typography.emphasis,
                        fontWeight: 600,
                        color: colors.foreground,
                      }}
                    >
                      {line.name}
                    </Typography>
                  ) : (
                    <TextField
                      initialValue={line.name}
                      onCommit={(v) => onLineName(cat.key, line.lineId, v)}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: typography.emphasis,
                        fontWeight: 600,
                      }}
                    />
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {line.type !== 'hourly' ? (
                      readOnly ? (
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: colors.foreground,
                          }}
                        >
                          {fmt(line.total ?? 0)}
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            sx={{
                              fontSize: typography.caption,
                              color: colors.mutedForeground,
                            }}
                          >
                            $
                          </Typography>
                          <NumberField
                            initialValue={line.total ?? ''}
                            onCommit={(raw) =>
                              onLine(cat.key, line.lineId, 'total', raw)
                            }
                            onFocus={() => {
                              if (resettable) setSuggLine(line.lineId);
                            }}
                            width={72}
                            fontSize={13}
                            color={colors.foreground}
                            underlineColor={colors.border}
                            align="right"
                          />
                        </Box>
                      )
                    ) : (
                      <Box
                        component={readOnly ? 'span' : 'button'}
                        type={readOnly ? undefined : 'button'}
                        onClick={
                          readOnly
                            ? undefined
                            : () => {
                                if (resettable) {
                                  setSuggLine(
                                    suggLine === line.lineId
                                      ? null
                                      : line.lineId
                                  );
                                }
                              }
                        }
                        sx={{
                          border: 'none',
                          background: 'none',
                          cursor: !readOnly && resettable ? 'pointer' : 'default',
                          fontFamily: 'inherit',
                          fontSize: typography.caption,
                          fontWeight: 700,
                          color: colors.success,
                          p: 0,
                        }}
                      >
                        {fmt(lt)}/mo
                      </Box>
                    )}
                    {readOnly ? null : (
                      <Box
                        component="button"
                        type="button"
                        onClick={() => {
                          if (suggLine === line.lineId) setSuggLine(null);
                          onDeleteLine(cat.key, line.lineId);
                        }}
                        sx={{
                          display: 'flex',
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          p: 0,
                        }}
                      >
                        <CloseIcon
                          sx={{ fontSize: 17, color: colors.destructive }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>

                {suggLine === line.lineId && onResetLine && resettable ? (
                  <Box
                    sx={{
                      backgroundColor: colors.accent,
                      borderRadius: 2.5,
                      p: 1.5,
                      mt: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      <RestoreIcon
                        sx={{
                          fontSize: 16,
                          color: colors.accentForeground,
                          mt: '1px',
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          flex: 1,
                          fontSize: typography.caption,
                          lineHeight: 1.5,
                          color: colors.accentForeground,
                        }}
                      >
                        <b>
                          Your monthly default:{' '}
                          {fmt(getLineDefault?.(cat.key, line.lineId) ?? 0)}
                        </b>
                        . You&apos;re using a custom amount of {fmt(lt)} for this
                        month.
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => {
                          onResetLine(cat.key, line.lineId);
                          setSuggLine(null);
                        }}
                        sx={{
                          flex: 1,
                          py: 1,
                          border: 'none',
                          borderRadius: 1.75,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          fontSize: typography.caption,
                          fontWeight: 700,
                          backgroundColor: colors.primary,
                          color: colors.primaryForeground,
                        }}
                      >
                        ✓ Use {fmt(getLineDefault?.(cat.key, line.lineId) ?? 0)}
                      </Box>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setSuggLine(null)}
                        sx={{
                          flex: 1,
                          py: 1,
                          borderRadius: 1.75,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          fontSize: typography.caption,
                          backgroundColor: colors.card,
                          border: `1px solid ${colors.border}`,
                          color: colors.mutedForeground,
                        }}
                      >
                        Keep mine
                      </Box>
                    </Box>
                  </Box>
                ) : null}

                {line.type === 'hourly' ? (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography sx={smallLabelSx}>$/hr</Typography>
                      {readOnly ? (
                        <Typography
                          sx={{ fontSize: 13, color: colors.foreground }}
                        >
                          {pctVal(line.wage ?? 0)}
                        </Typography>
                      ) : (
                        <NumberField
                          initialValue={line.wage ?? ''}
                          onCommit={(raw) =>
                            onLine(cat.key, line.lineId, 'wage', raw)
                          }
                          width={44}
                          fontSize={13}
                          color={colors.foreground}
                          underlineColor={colors.border}
                        />
                      )}
                    </Box>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography sx={smallLabelSx}>hrs/mo</Typography>
                      {readOnly ? (
                        <Typography
                          sx={{ fontSize: 13, color: colors.foreground }}
                        >
                          {fmtN(line.hours ?? 0)}
                        </Typography>
                      ) : (
                        <NumberField
                          initialValue={line.hours ?? ''}
                          onCommit={(raw) =>
                            onLine(cat.key, line.lineId, 'hours', raw)
                          }
                          width={44}
                          fontSize={13}
                          color={colors.foreground}
                          underlineColor={colors.border}
                        />
                      )}
                    </Box>
                    <Typography sx={smallLabelSx}>= {fmt(lt)}</Typography>
                  </Box>
                ) : null}
              </Box>
            );
          })}

          {readOnly ? null : (
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              {cat.key === 'labor' ? (
                <AddButton
                  label="+ Add employee"
                  onClick={() => onAddLine(cat.key, 'hourly')}
                />
              ) : null}
              <AddButton
                label={
                  cat.key === 'labor' ? '+ Add fixed cost' : '+ Add line item'
                }
                onClick={() => onAddLine(cat.key, 'fixed')}
              />
            </Box>
          )}

          {hasLines ? (
            <Box
              sx={{
                mt: 1.25,
                backgroundColor: colors.muted,
                borderRadius: 2,
                p: 1.25,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  color: colors.mutedForeground,
                  fontSize: typography.caption,
                }}
              >
                {rangeLabel} cost
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: colors.foreground,
                  fontSize: typography.caption,
                }}
              >
                {fmt(scaledTotal)} ·{' '}
                {totalRevenue > 0
                  ? pct((scaledTotal / totalRevenue) * 100)
                  : pct(0)}{' '}
                of revenue
              </Typography>
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

function AddButton({ label, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        flex: 1,
        py: 1,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: typography.caption,
        fontWeight: 600,
        color: colors.primary,
        backgroundColor: colors.accent,
        border: `1px dashed ${colors.primary}`,
        borderRadius: 2,
      }}
    >
      {label}
    </Box>
  );
}

function FoodCard({
  cat,
  rangeLabel,
  expanded,
  onToggle,
  readOnly,
  onFoodOverride,
  onResetFood,
  onEditItemCosts,
}) {
  const monthly = catMonthly(cat);
  const scaledTotal = cat.scaledLinesTotal ?? monthly;
  const itemsPct = cat.itemsPct ?? cat.totalPct;
  const wastePct = cat.wastePct ?? 0;
  const wasteCost = cat.wasteCost ?? 0;
  const itemsCost = cat.itemsCost ?? scaledTotal;
  const itemRevenue = itemsPct > 0 ? (itemsCost / itemsPct) * 100 : 0;
  const wasteNeg = wastePct < 0;

  return (
    <Box sx={{ ...cardSx, mb: 1.25, overflow: 'hidden' }}>
      <Box sx={headerRowSx}>
        <ToggleArea onToggle={onToggle}>
          <IconWrap icon={ShoppingBagOutlinedIcon} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={catLabelSx}>{cat.label}</Typography>
            <Typography sx={catSubSx}>
              {fmt(scaledTotal)} ({rangeLabel}) ·{' '}
              {cat.isOverridden ? 'adjusted total' : 'from menu items'}
            </Typography>
          </Box>
        </ToggleArea>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {readOnly ? (
            <Typography sx={{ fontWeight: 700, color: colors.primary }}>
              {pct(cat.totalPct)}
            </Typography>
          ) : (
            <>
              <NumberField
                initialValue={pctVal(cat.totalPct)}
                onCommit={onFoodOverride}
                width={48}
                fontSize={14}
              />
              <Typography sx={{ fontWeight: 700, color: colors.primary }}>
                %
              </Typography>
            </>
          )}
          <Box
            component="button"
            type="button"
            onClick={onToggle}
            sx={{
              display: 'flex',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              p: 0,
            }}
          >
            <Chevron expanded={expanded} />
          </Box>
        </Box>
      </Box>

      {expanded ? (
        <Box sx={expandSx}>
          <FoodLine
            title="From menu items"
            sub="Sum of each item's food cost"
            value={pct(itemsPct)}
            dollar={fmt(itemsCost)}
          />

          <FoodLine
            title="Other / Waste"
            sub="Spoilage, comps, prep loss, untracked"
            value={`${wastePct > 0 ? '+' : ''}${pct(wastePct)}`}
            dollar={fmt(wasteCost)}
            borderColor={
              wastePct !== 0
                ? wasteNeg
                  ? colors.success
                  : colors.warning
                : undefined
            }
            valueColor={
              wasteNeg
                ? colors.success
                : wastePct > 0
                  ? colors.warningSurfaceForeground
                  : colors.faint
            }
            dollarColor={wasteNeg ? colors.success : colors.mutedForeground}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              p: 1.25,
              backgroundColor: colors.accent,
              borderRadius: 2,
              mb: 1.25,
            }}
          >
            <Typography
              sx={{
                fontSize: typography.caption,
                fontWeight: 700,
                color: colors.accentForeground,
              }}
            >
              Total Food &amp; Beverage
            </Typography>
            <Typography
              sx={{
                fontSize: typography.body,
                fontWeight: 800,
                color: colors.accentForeground,
              }}
            >
              {pct(cat.totalPct)} · {fmt(scaledTotal)}
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: typography.caption,
              color: colors.mutedForeground,
              lineHeight: 1.35,
              mb: 1.25,
            }}
          >
            {readOnly
              ? `${fmt(itemsCost)} from your menu items (${pct(
                  itemsPct
                )} of ${fmt(itemRevenue)} of item sales) plus ${fmt(
                  wasteCost
                )} of Other/Waste (${pct(wastePct)}) is ${fmt(
                  scaledTotal
                )}. Change it from the item costs, or from the F&B total in Settings.`
              : cat.isOverridden
                ? `You set total F&B to ${pct(cat.totalPct)}. Menu items account for ${pct(itemsPct)}, and the ${pct(wastePct)} difference shows as Other/Waste.`
                : `This comes from your menu item costs (${pct(itemsPct)}). If your real F&B cost differs (e.g. waste), edit the % above to set it — the gap becomes an Other/Waste line.`}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box
              component="button"
              type="button"
              onClick={onEditItemCosts}
              sx={{
                flex: 1,
                py: 1,
                border: 'none',
                borderRadius: 2,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: typography.caption,
                fontWeight: 600,
                backgroundColor: colors.primary,
                color: colors.primaryForeground,
              }}
            >
              Edit item costs →
            </Box>
            {!readOnly && cat.isOverridden ? (
              <Box
                component="button"
                type="button"
                onClick={onResetFood}
                sx={{
                  flex: 1,
                  py: 1,
                  borderRadius: 2,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: typography.caption,
                  fontWeight: 600,
                  backgroundColor: colors.card,
                  border: `1px solid ${colors.border}`,
                  color: colors.mutedForeground,
                }}
              >
                Reset to menu items
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

function FoodLine({
  title,
  sub,
  value,
  dollar,
  borderColor,
  valueColor,
  dollarColor,
}) {
  return (
    <Box
      sx={{
        backgroundColor: colors.card,
        borderRadius: 2,
        p: 1.25,
        mb: 1,
        border: `1px solid ${borderColor ?? colors.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box>
        <Typography
          sx={{
            fontSize: typography.caption,
            fontWeight: 600,
            color: colors.foreground,
          }}
        >
          {title}
        </Typography>
        <Typography sx={smallLabelSx}>{sub}</Typography>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography
          sx={{
            fontSize: typography.body,
            fontWeight: 800,
            color: valueColor ?? colors.foreground,
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            fontSize: typography.caption,
            color: dollarColor ?? colors.mutedForeground,
          }}
        >
          {dollar}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * The cost breakdown, shown either as a report or as an editor.
 *
 * `readOnly` takes no handlers at all, which is what keeps the two callers
 * honest: My Costs reports the figures, Settings is where they are entered.
 * `onEditItemCosts` is the exception — it navigates rather than edits, so both
 * callers offer it.
 */
export default function CostEditor({
  cats,
  totalRevenue,
  rangeLabel,
  salesTax,
  readOnly = false,
  onCatPct,
  onFoodOverride,
  onResetFood,
  onLine,
  onLineName,
  onAddLine,
  onDeleteLine,
  onSalesTax,
  onEditItemCosts,
  isLineResettable,
  onResetLine,
  getLineDefault,
}) {
  const [expanded, setExpanded] = useState(null);

  return (
    <Box>
      {cats.map((cat) =>
        cat.isReadOnly ? (
          <ReadOnlyCard key={cat.key} cat={cat} rangeLabel={rangeLabel} />
        ) : cat.isDerived || cat.key === 'food' ? (
          <FoodCard
            key={cat.key}
            cat={cat}
            rangeLabel={rangeLabel}
            expanded={expanded === cat.key}
            onToggle={() => setExpanded(expanded === cat.key ? null : cat.key)}
            readOnly={readOnly}
            onFoodOverride={onFoodOverride}
            onResetFood={onResetFood}
            onEditItemCosts={onEditItemCosts}
          />
        ) : (
          <CatCard
            key={cat.key}
            cat={cat}
            totalRevenue={totalRevenue}
            rangeLabel={rangeLabel}
            expanded={expanded === cat.key}
            onToggle={() => setExpanded(expanded === cat.key ? null : cat.key)}
            readOnly={readOnly}
            onCatPct={onCatPct}
            onLine={onLine}
            onLineName={onLineName}
            onAddLine={onAddLine}
            onDeleteLine={onDeleteLine}
            isLineResettable={isLineResettable}
            onResetLine={onResetLine}
            getLineDefault={getLineDefault}
          />
        )
      )}

      {/* Sales tax is a pass-through. It is collected from customers and paid
          to the state, so it never reduces profit — which is exactly why it is
          shown apart from the cost categories above. */}
      <Box
        sx={{
          ...cardSx,
          mb: 1.25,
          overflow: 'hidden',
          border: `1px solid ${colors.warning}`,
        }}
      >
        <Box sx={headerRowSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              flex: 1,
              minWidth: 0,
            }}
          >
            <IconWrap
              icon={PercentIcon}
              tone={{
                bg: colors.warningSurface,
                fg: colors.warningSurfaceForeground,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={catLabelSx}>Sales Tax</Typography>
              <Typography sx={catSubSx}>
                {fmt(salesTax.collected)} collected · {rangeLabel}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {readOnly ? (
              <Typography
                sx={{ fontWeight: 700, color: colors.warningSurfaceForeground }}
              >
                {pct(salesTax.pct)}
              </Typography>
            ) : (
              <>
                <NumberField
                  initialValue={pctVal(salesTax.pct)}
                  onCommit={onSalesTax}
                  width={48}
                  fontSize={14}
                  color={colors.warningSurfaceForeground}
                  underlineColor={colors.warning}
                />
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: colors.warningSurfaceForeground,
                  }}
                >
                  %
                </Typography>
              </>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            borderTop: `1px solid ${colors.warning}`,
            backgroundColor: colors.warningSurface,
            p: 1.75,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <TaxRow
            label="Customers paid (incl. tax)"
            value={fmt(salesTax.customerPaid)}
          />
          {/* Taken out of what customers paid, so it reads as a deduction. */}
          <TaxRow
            label="Tax remitted to state"
            value={fmt(-salesTax.collected)}
          />
          <TaxRow
            label="Your sales (revenue)"
            value={fmt(salesTax.revenue)}
            strong
            sx={{ borderTop: `1px solid ${colors.warning}`, pt: 0.5 }}
          />
          <Typography
            sx={{
              fontSize: typography.caption,
              color: colors.warningSurfaceForeground,
              mt: 1,
              lineHeight: 1.35,
            }}
          >
            Sales tax is collected from customers and paid to the state — it is
            not your money and does not reduce profit.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function TaxRow({ label, value, strong, sx }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, ...sx }}>
      <Typography
        sx={{
          fontSize: typography.caption,
          color: colors.warningSurfaceForeground,
          fontWeight: strong ? 600 : 400,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: typography.caption,
          fontWeight: strong ? 800 : 700,
          color: colors.warningSurfaceForeground,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
